import { createContext, useContext } from 'react'
import { useQuery } from 'react-query'
import { Draft, PaginatedResponse } from 'backoffice/components/Drafts/types/types'
import { authClient } from 'auth'

const apiUrl = process.env.REACT_APP_API_V2 ?? ''

/**
 * Max drop IDs per request (API enforces maxItems: 50).
 * Change this variable to adjust batch size.
 */
export const DRAFTS_BATCH_SIZE = 50

/**
 * Builds the full request URL with all query params encoded via URLSearchParams.
 */
const buildDraftsUrl = (dropIds: number[]): string => {
  const params = new URLSearchParams()
  params.set('limit', String(dropIds.length))
  params.set('offset', '0')
  for (const id of dropIds) {
    params.append('dropIds[]', String(id))
  }
  return `${apiUrl}/drop-requests/admin/drafts?${params.toString()}`
}

/**
 * Fetches drafts for an array of drop IDs in a single API call.
 * Uses the dropIds[] array query param format.
 * Returns a Map of dropId -> Draft for quick lookup.
 */
const fetchDraftsByDropIds = async (dropIds: number[]): Promise<Map<number, Draft>> => {
  const draftsMap = new Map<number, Draft>()
  if (dropIds.length === 0) return draftsMap
  if (dropIds.length > DRAFTS_BATCH_SIZE) {
    throw new Error(`fetchDraftsByDropIds: received ${dropIds.length} IDs, max allowed is ${DRAFTS_BATCH_SIZE}`)
  }

  let bearer: string | false
  try {
    bearer = await authClient.getAPIToken()
  } catch {
    bearer = false
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json; charset=utf-8',
    accept: '*/*',
  }
  if (bearer) {
    headers['Authorization'] = `Bearer ${bearer}`
  }

  const url = buildDraftsUrl(dropIds)

  const response = await fetch(url, { method: 'GET', headers })

  if (!response.ok) {
    let errorBody = ''
    try {
      errorBody = await response.text()
    } catch {
      // Ignore errors while reading the error body
    }

    const messageParts = [
      `Failed to fetch drafts (status ${response.status} ${response.statusText})`,
      errorBody && `Response body: ${errorBody}`,
    ].filter(Boolean)

    throw new Error(messageParts.join(' - '))
  }

  const data: PaginatedResponse<Draft> = await response.json()
  for (const draft of data.items) {
    if (draft.dropId != null) {
      draftsMap.set(draft.dropId, draft)
    }
  }
  return draftsMap
}

/**
 * Fetches drafts for multiple drop IDs, batching into groups of DRAFTS_BATCH_SIZE.
 * Each batch is a single API call using the dropIds[] array param.
 * Returns a merged Map<number, Draft> across all batches.
 */
const getDraftsByDropIds = async (dropIds: number[]): Promise<Map<number, Draft>> => {
  const draftsMap = new Map<number, Draft>()

  const batches: number[][] = []
  for (let i = 0; i < dropIds.length; i += DRAFTS_BATCH_SIZE) {
    batches.push(dropIds.slice(i, i + DRAFTS_BATCH_SIZE))
  }

  const results = await Promise.all(batches.map((batch) => fetchDraftsByDropIds(batch)))

  for (const batchResult of results) {
    batchResult.forEach((draft, dropId) => {
      draftsMap.set(dropId, draft)
    })
  }

  return draftsMap
}

// --- Context for sharing prefetched drafts ---

interface DraftsMapContextValue {
  draftsMap: Map<number, Draft>
  isLoading: boolean
}

export const DraftsMapContext = createContext<DraftsMapContextValue>({
  draftsMap: new Map(),
  isLoading: false,
})

export const useDraftsMap = () => useContext(DraftsMapContext)

/**
 * Hook to batch-fetch drafts for all petition drop IDs on the current page.
 * Groups drop IDs into batches of DRAFTS_BATCH_SIZE and fetches them in parallel.
 * Returns a Map<number, Draft> for quick lookup.
 */
export const useBatchDraftsForPetitions = (dropIds: number[]) => {
  const sortedIds = [...new Set(dropIds)].sort((a, b) => a - b)

  return useQuery(['batchDraftsForPetitions', sortedIds], () => getDraftsByDropIds(sortedIds), {
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook used by DraftReference to get the draft for a specific dropId
 * from the prefetched batch context. No additional network request needed.
 */
export const usePrefetchedDraft = (dropId: number | undefined) => {
  const { draftsMap, isLoading } = useDraftsMap()
  const draft = dropId ? draftsMap.get(dropId) ?? null : null

  return {
    data: draft,
    isLoading,
  }
}

/**
 * Fetch the draft associated with a selected drop ID (used by search).
 * Uses the dropIds[] array format with a single item.
 */
export const useSearchDraftByDropId = (dropId: number | null) => {
  return useQuery(
    ['searchDraftByDropId', dropId],
    async () => {
      if (!dropId) return null
      const result = await fetchDraftsByDropIds([dropId])
      return result.get(dropId) ?? null
    },
    {
      enabled: !!dropId,
      staleTime: 30 * 1000,
      keepPreviousData: true,
    },
  )
}
